import { Alert, Button, Input, Modal, Table, Tag, Typography, message } from "antd";
import { useState } from "react";
import { bulkAuthorizePaymentsFromSheet } from "../../apis/procurement.api";

type BulkAuthorizeSheetModalProps = {
  visible: boolean;
  onCancel: () => void;
  onCompleted: () => void;
};

type PreviewRow = {
  row_number: number;
  action: string;
  reject_reason?: string | null;
  id: number;
  lead_id?: string | null;
  payment_amount: string;
  status: string;
  current: Record<string, any>;
  changes: Record<string, { from: any; to: any }>;
};

type SheetError = {
  row_number?: number;
  lead_id?: string | null;
  payment_id?: string | null;
  error: string;
};

const { Text } = Typography;

const BulkAuthorizeSheetModal = ({
  visible,
  onCancel,
  onCompleted,
}: BulkAuthorizeSheetModalProps) => {
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetTab, setSheetTab] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [errors, setErrors] = useState<SheetError[]>([]);
  const [planToken, setPlanToken] = useState<string | null>(null);
  const [counts, setCounts] = useState<{ authorize: number; reject: number }>({
    authorize: 0,
    reject: 0,
  });

  const resetPreview = () => {
    setRows([]);
    setErrors([]);
    setPlanToken(null);
    setCounts({ authorize: 0, reject: 0 });
  };

  const closeAndReset = () => {
    resetPreview();
    setSheetUrl("");
    setSheetTab("");
    onCancel();
  };

  const handlePreview = async () => {
    if (!sheetUrl.trim()) {
      message.error("Paste the Google Sheet link first");
      return;
    }
    setPreviewing(true);
    resetPreview();
    const res = await bulkAuthorizePaymentsFromSheet(
      sheetUrl.trim(),
      sheetTab.trim() || undefined,
      true
    );
    setPreviewing(false);

    if (res?.error) {
      setErrors(res.errors ?? []);
      message.error(res.error);
      return;
    }

    setRows(res?.rows ?? []);
    setErrors(res?.errors ?? []);
    setPlanToken(res?.plan_token ?? null);
    setCounts({
      authorize: res?.authorize_count ?? 0,
      reject: res?.reject_count ?? 0,
    });

    if (!res?.rows?.length) {
      message.warning("No valid rows found in this sheet");
    }
  };

  const handleApply = async () => {
    if (!planToken) {
      message.error("Preview the sheet before applying it");
      return;
    }
    setApplying(true);
    const res = await bulkAuthorizePaymentsFromSheet(
      sheetUrl.trim(),
      sheetTab.trim() || undefined,
      false,
      planToken
    );
    setApplying(false);

    if (res?.error) {
      // 409 = the sheet changed since it was previewed; force a fresh preview.
      if (res.status === 409) {
        resetPreview();
      }
      setErrors(res.errors ?? []);
      message.error(res.error);
      return;
    }

    message.success(
      res?.message ?? `${res?.queued_count ?? 0} row(s) queued for processing`
    );
    closeAndReset();
    onCompleted();
  };

  const columns = [
    { title: "Row", dataIndex: "row_number", key: "row_number", width: 70 },
    { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", width: 120 },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: 110,
      render: (action: string, record: PreviewRow) => (
        <>
          <Tag color={action === "authorize" ? "green" : "red"}>
            {action?.toUpperCase()}
          </Tag>
          {record.reject_reason && (
            <div>
              <Text type="secondary">{record.reject_reason}</Text>
            </div>
          )}
        </>
      ),
    },
    {
      title: "Amount",
      dataIndex: "payment_amount",
      key: "payment_amount",
      width: 120,
      render: (value: string) => `₹${(Number(value) / 100).toFixed(2)}`,
    },
    {
      title: "Changes",
      key: "changes",
      render: (_: any, record: PreviewRow) => {
        const entries = Object.entries(record.changes ?? {});
        if (!entries.length) return <Text type="secondary">No field changes</Text>;
        return entries.map(([field, change]) => (
          <div key={field}>
            <Text strong>{field}</Text>: {String(change.from ?? "—")} →{" "}
            {String(change.to ?? "—")}
          </div>
        ));
      },
    },
  ];

  const errorColumns = [
    { title: "Row", dataIndex: "row_number", key: "row_number", width: 70 },
    { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", width: 120 },
    { title: "Payment ID", dataIndex: "payment_id", key: "payment_id", width: 160 },
    { title: "Problem", dataIndex: "error", key: "error" },
  ];

  return (
    <Modal
      title="Authorize payments from sheet"
      open={visible}
      onCancel={closeAndReset}
      width={1000}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={closeAndReset}>
          Cancel
        </Button>,
        <Button key="preview" onClick={handlePreview} loading={previewing}>
          {rows.length || errors.length ? "Re-preview" : "Preview"}
        </Button>,
        <Button
          key="apply"
          type="primary"
          onClick={handleApply}
          loading={applying}
          disabled={!planToken || !rows.length}
        >
          Apply {rows.length ? `(${rows.length})` : ""}
        </Button>,
      ]}
    >
      <Input
        placeholder="Google Sheet link"
        value={sheetUrl}
        onChange={(e) => {
          setSheetUrl(e.target.value);
          resetPreview();
        }}
        className="mb-2"
      />
      <Input
        placeholder="Tab name (optional — defaults to the first tab)"
        value={sheetTab}
        onChange={(e) => {
          setSheetTab(e.target.value);
          resetPreview();
        }}
        className="mb-3"
      />

      <Alert
        type="info"
        showIcon
        className="mb-3"
        message="The sheet must be shared with the service account. Preview first — applying runs in the background and emails a summary when it finishes."
      />

      {(rows.length > 0 || errors.length > 0) && (
        <div className="mb-2">
          <Tag color="green">{counts.authorize} to authorize</Tag>
          <Tag color="red">{counts.reject} to reject</Tag>
          {errors.length > 0 && <Tag color="orange">{errors.length} skipped</Tag>}
        </div>
      )}

      {rows.length > 0 && (
        <Table
          size="small"
          rowKey="row_number"
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 800 }}
        />
      )}

      {errors.length > 0 && (
        <>
          <Text strong>Rows that will be skipped</Text>
          <Table
            size="small"
            rowKey={(record: SheetError, index?: number) =>
              `${record.row_number ?? "x"}-${index}`
            }
            columns={errorColumns}
            dataSource={errors}
            pagination={{ pageSize: 5 }}
            scroll={{ x: 700 }}
          />
        </>
      )}
    </Modal>
  );
};

export default BulkAuthorizeSheetModal;
